import { useNavigate } from "react-router-dom";
import useAppState from "../../../context/AppContext";
import { APP_RETIREMENT_URLS, ERROR_MESSAGES, RETIRED_TYPE_CODE } from "../../../const/AppConst";
import StickButtonsBloc from "../../common/StickButtonsBloc";
import { useForm } from "react-hook-form";
import * as Yup from 'yup';
import InfoContactTemplate, { InfoContactFormData } from "../../../components/info-perso-template/InfoContactTemplate";
import { yupResolver } from "@hookform/resolvers/yup";
import { createRetirementProposition, validEmail } from "../../../services/CustomerApiService";
import { AxiosError } from 'axios';
import { IApiErrors } from "../../../context/model";
import { getBodyRequestCreateREProposition, getInfoRetirementSteps } from "../../../utils/utils";
import { useState } from "react";
import MgpMessage from "../../../elements/MgpMessage/MgpMessage";
import LoaderModal from "../../../modals/common/LoaderModal";
import GeneralDataProtectionBloc from "../../common/GeneralDataProtectionBloc";


export default function REInfoContactBloc() {

  const navigate = useNavigate();

  const {
    state: { updatedProposition, jwtToken, proposition, infoSteps, additionalData },
    dispatch,
  } = useAppState();

  const [errorMsg, setErrorMsg] = useState<string | null>();
  const [loading, setLoading] = useState(false);

  const onValidEmail = async (email: string) => {
    try {
      const token = jwtToken!.accessToken;
      const res = await validEmail(token, email);
      if (res.data.valid !== undefined && res.data.valid === true) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      const err = error as AxiosError;
      console.log(err);
      return true;
    }
  }
   const validationSchema = Yup.object().shape({
      email: Yup.string()
      .required(ERROR_MESSAGES.requiredMsg)
      .email(ERROR_MESSAGES.invalidMsg)
      .test("is-email-valid", ERROR_MESSAGES.invalidEmailMsg, async (value, values) => {
        if(!value) {
          return false;
        }
        const valid = await onValidEmail(value);
        return valid;
      }),
      phoneNumber: Yup.string()
        .when('isForeignMobile', {
            is: false,
            then: Yup.string()
                    .required(ERROR_MESSAGES.requiredMsg)
                    .matches(/^(0[6-7])([ .-]?\d{2}){4}$/, ERROR_MESSAGES.invalidFrenchMobile)
                    .min(14, ERROR_MESSAGES.invalidMsg),
        })
        .when('isForeignMobile', {
            is: true,
            then: Yup.string()
                    .required(ERROR_MESSAGES.requiredMsg)
                    .matches(/^(?:\+|00)[0-9]\d{1,14}$/, ERROR_MESSAGES.invalidForeignMobile)
                    .test("number-is-french", ERROR_MESSAGES.invalidFrenchMobileWithIndicator, async (value, values) => {
                      if(!value) {
                        return false;
                      }
                      const isFrenchNumber = value.startsWith("0033") || value.startsWith("+33");
                      return !isFrenchNumber;
                    }),         
        }),
      commercialTelephoneAccept: Yup.string().required(ERROR_MESSAGES.requiredMsg),
      commercialEmailAccept: Yup.string().required(ERROR_MESSAGES.requiredMsg),
      commercialSmsAccept: Yup.string().required(ERROR_MESSAGES.requiredMsg),
    });

  const {
    register,
    handleSubmit,
    watch,
    setError,
    setValue,
    formState: { errors },
  } = useForm<InfoContactFormData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      email: updatedProposition?.email ? updatedProposition.email : "",
      showCommercialMgpChoice: true,
      isForeignMobile: updatedProposition?.isForeignMobile ? updatedProposition.isForeignMobile : false,
      phoneNumber: updatedProposition?.phoneNumber && !updatedProposition?.isForeignMobile ? updatedProposition.phoneNumber.replace(/[^\dA-Z]/g, '').replace(/(.{2})/g, '$1 ').trim(): updatedProposition?.phoneNumber && updatedProposition?.isForeignMobile ? updatedProposition?.phoneNumber : "",
      commercialEmailAccept: updatedProposition?.consent?.commercialEmailAccept !== undefined ? updatedProposition.consent.commercialEmailAccept.toString() : undefined,
      commercialSmsAccept: updatedProposition?.consent?.commercialSmsAccept !== undefined ? updatedProposition.consent.commercialSmsAccept.toString() : undefined,
      commercialTelephoneAccept: updatedProposition?.consent?.commercialTelephoneAccept !== undefined ? updatedProposition.consent.commercialTelephoneAccept.toString() : undefined,
      commercialPartnerAccept: updatedProposition?.consent?.commercialPartnerAccept !== undefined ? updatedProposition.consent.commercialPartnerAccept.toString() : undefined,
    },
    resolver: yupResolver(validationSchema),
  });

  const onNextAction = async (data: InfoContactFormData) => {
    try {
      let tempUpdatedPropo = updatedProposition;
      let propositionDetail = proposition ;

      let response;
      if (!proposition?.propositionId) {
        setLoading(true);
        const bodyData = getBodyRequestCreateREProposition(tempUpdatedPropo!, data);
        response = await createRetirementProposition(jwtToken?.accessToken!, bodyData);

      }
     
      if(tempUpdatedPropo) {
        tempUpdatedPropo.email = data.email;
        tempUpdatedPropo.phoneNumber = data.phoneNumber.replace(/ /g, '');
        tempUpdatedPropo.isForeignMobile = data.isForeignMobile;
        tempUpdatedPropo.consent = {
          showCommercialMgpChoice: true,
          commercialEmailAccept: data.commercialEmailAccept === 'true',
          commercialSmsAccept: data.commercialSmsAccept === 'true',
          commercialTelephoneAccept: data.commercialTelephoneAccept === 'true',
          commercialPartnerAccept: false,   
        }
      const nextUrl = updatedProposition?.hasOldMutual ? 
        APP_RETIREMENT_URLS.infoOldContractPage : 
        APP_RETIREMENT_URLS.infoBankPage;

      
      if (!proposition?.propositionId && 
      response && 
      response.data &&
      propositionDetail) {
        // these values to activate API step
          propositionDetail.propositionId = response.data?.propositionId;
          propositionDetail.propositionName = response.data?.propositionName;
          propositionDetail.affiliationType = RETIRED_TYPE_CODE;
          propositionDetail.configuration = response.data?.configuration;
     
        const hasIdentityDocStep = getInfoRetirementSteps(
          infoSteps, 
          "INFO_IDENTITY_DOC", 
          response?.data?.configuration?.requireIdentityDocument ?? false,
          (additionalData?.hasChild || additionalData?.hasPartner) ? "INFO_BENE" : "INFO_ATTES_RO"
         );
      
        if (hasIdentityDocStep.hasChanged) {
          dispatch({type: "UPDATE_STEPS", 
            steps: hasIdentityDocStep.infoSteps
          })
        }
      }

      dispatch({
        type: 'NEXT_INFO_STEP', 
        path: nextUrl, 
        updatedProposition: tempUpdatedPropo, 
        proposition: proposition?.propositionId ? proposition : propositionDetail
      });

      navigate({
          pathname: nextUrl,
          search: `?id=${proposition!.propositionId}`,
      }); 
      }
    } catch (error) {
      const err = error as AxiosError;
      console.log(err);
      setLoading(false);
      if(err.response && err.response.status == 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else {
        setErrorMsg("Une erreur technique est survenue. Veuillez contacter notre service client.");
      }
    }

  }

  const onPreviousAction = async () => {
    const url = APP_RETIREMENT_URLS.infoAddressPage
   
    dispatch({type: 'BACK_INFO_STEP', path: url});
    navigate({
        pathname: url,
        search: `?id=${proposition!.propositionId}`,
    });         
  }


  return (
    <form style={{width: '100%'}}>
      <div>
        {errorMsg && <MgpMessage type="danger" message={errorMsg} hasCloseBtn={true}/>}
      </div>
      <div className='form-row page-content'>
        <InfoContactTemplate 
          register={register} 
          errors={errors} 
          watch={watch}
          showCommercialMgpChoice={true}
          setValue={setValue}
          setError={setError}
           />  
      </div>
       {loading && <LoaderModal hasMessage={true}/>}
      <StickButtonsBloc 
        showPreviousButton={true} 
        disabledPreviousButton={false} 
        nextAction={handleSubmit(onNextAction)} 
        showNextButton={true} 
        previousAction={onPreviousAction}
      />
      <GeneralDataProtectionBloc/>
    </form>
  )
}